import {
  CustomDropdown,
  FlatButton,
  Icon,
  Typography,
} from '@globalmed/atlas-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LanguageDropdown.module.css';
import { useLanguageContext } from '@/features/LanguageContext';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ELanguage } from '@/types';
import { translatedLanguageOptions } from '@/utils/languageOptions';
import cn from 'classnames';

type TProps = {
  className?: string;
};

/**
 * LanguageDropdown component allowing user to select a language.
 * Displays current language with flag and title, and opens a dropdown
 * to select a different language.
 * @param props - Component props
 * @param props.className - Optional additional class name for styling
 * @returns JSX element for language selection dropdown.
 */
export const LanguageDropdown = ({ className }: TProps) => {
  const { language, setLanguage } = useLanguageContext();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languageOptions = translatedLanguageOptions(t);
  const selectedLanguageOption =
    languageOptions.find((item) => item.id === language) ??
    languageOptions.at(0);

  const handleClick = () => setIsOpen((prev) => !prev);

  const handleValueClick = (lang: ELanguage) => {
    setLanguage?.(lang);
    setIsOpen(false);
  };

  const ref = useOutsideClick<HTMLDivElement>(() => setIsOpen(false));

  return (
    <div
      data-testid="language-dropdown"
      className={cn(styles.container, className)}
      ref={ref}
    >
      <div className={styles.dropdown}>
        <FlatButton
          className={styles.trigger}
          onClick={handleClick}
          type="button"
          aria-label={selectedLanguageOption?.title}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <img
            src={selectedLanguageOption?.image}
            alt=""
            className={styles.image}
          />
          <Typography fontWeight={600}>
            {selectedLanguageOption?.title}
          </Typography>
          <Icon name="ChevronDown" size="20" />
        </FlatButton>
        {isOpen && (
          <CustomDropdown
            selected={[language]}
            onValueClick={handleValueClick}
            sx={{ width: '200px' }}
          >
            {languageOptions.map((item) => (
              <CustomDropdown.Item key={item.id} {...item} />
            ))}
          </CustomDropdown>
        )}
      </div>
    </div>
  );
};
